/**
 * Shared, app-wide accent color palette.
 *
 * This is the base color layer: the design system's
 * `design-system/constants/colors.ts` re-exports these tokens for use inside
 * design-system components, and feature code outside the design system
 * (e.g. app-care, app-emr) should import directly from here instead of
 * reaching into design-system internals.
 */
export type AccentColorName =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'magenta'
  | 'goldenPurple'
  | 'geekBlue'
  | 'daybreakBlue'
  | 'cyan'
  | 'polarGreen'
  | 'lime'
  | 'calendulaGold'
  | 'sunsetOrange'
  | 'volcano'
  | 'dustRed'
  | 'gray'

/**
 * Full-strength accent color for each predefined token. Surface and border
 * colors for a tinted treatment are derived from these at fixed alpha levels
 * (see `ACCENT_SURFACE_ALPHA` / `ACCENT_BORDER_ALPHA`). Values mirror the
 * `icons/tag/*` design tokens.
 */
export const ACCENT_COLORS: Record<AccentColorName, string> = {
  primary: '#507ee6',
  success: '#2e9b6a',
  error: '#cf1322',
  warning: '#faad14',
  info: '#1677ff',
  magenta: '#c41d7f',
  goldenPurple: '#6e3db0',
  geekBlue: '#3d5ec4',
  daybreakBlue: '#1677ff',
  cyan: '#13c2c2',
  polarGreen: '#2e9b6a',
  lime: '#a0d911',
  calendulaGold: '#faad14',
  sunsetOrange: '#fa8c16',
  volcano: '#fa541c',
  dustRed: '#cf1322',
  gray: '#434343'
}

/** Alpha applied to the accent color to produce a tinted surface (background). */
export const ACCENT_SURFACE_ALPHA = 0.1

/** Alpha applied to the accent color to produce a (lighter) border. */
export const ACCENT_BORDER_ALPHA = 0.4
